// src/pages/TraceabilityPage.js
import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Modal, Button } from 'react-bootstrap';
import TraceabilitySearchPanel from '../components/TraceabilitySearchPanel';
import TraceabilityForm from '../components/TraceabilityForm';
import TraceabilityTable from '../components/TraceabilityTable';
import Pagination from '../components/Pagination';
import PrintTraceabilityView from '../components/PrintTraceabilityView';
import { useTraceability } from '../hooks/useTraceability';
import service from '../services/traceabilityService';
import ingredientInventoryService from '../services/ingredientInventoryService';
import { usePermissions } from '../context/PermissionContext';

export default function TraceabilityPage() {
  const token = localStorage.getItem('token');
  const { hasModule, companySettings } = usePermissions();
  const companyLogoUrl = companySettings?.logoUrl || null;
  const companyName = companySettings?.name || '';
  const companyAddress = [companySettings?.addressLine1, companySettings?.city, companySettings?.stateProvince, companySettings?.postalCode].filter(Boolean).join(', ');

  // Search panel state
  const [searchLotCode, setSearchLotCode] = useState('');
  const [productSearchValue, setProductSearchValue] = useState('');
  const [customerSearchValue, setCustomerSearchValue] = useState('');

  // Debounced values sent to the backend (400 ms)
  const [debouncedSearch, setDebouncedSearch] = useState({});
  useEffect(() => {
    const t = setTimeout(() => {
      const s = {};
      if (searchLotCode.trim()) s.lotCode = searchLotCode.trim();
      if (productSearchValue.trim()) s.productName = productSearchValue.trim();
      if (customerSearchValue.trim()) s.customer = customerSearchValue.trim();
      setDebouncedSearch(s);
    }, 400);
    return () => clearTimeout(t);
  }, [searchLotCode, productSearchValue, customerSearchValue]);

  const { logs, page, totalPages, setPage, fetchPage } = useTraceability(20, debouncedSearch);

  // Ingredient search state (lot code)
  const [ingredientLotCode, setIngredientLotCode] = useState('');
  const [ingredientBreakdown, setIngredientBreakdown] = useState([]);
  const [ingredientProducts, setIngredientProducts] = useState([]);
  const [isIngredientSearchActive, setIsIngredientSearchActive] = useState(false);
  const [receivingLogs, setReceivingLogs] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [ingredientTotalQtyUsed, setIngredientTotalQtyUsed] = useState(null);

  // Ingredient name search state
  const [ingredientNameInput, setIngredientNameInput] = useState('');
  const [isIngredientNameActive, setIsIngredientNameActive] = useState(false);
  const [ingredientNameLabel, setIngredientNameLabel] = useState('');

  // Batch lot code search state
  const [batchLotCodeInput, setBatchLotCodeInput] = useState('');
  const [batchResult, setBatchResult] = useState(null); // { batchLotCode, batchDate, products, allIngredients }
  const [isBatchSearchActive, setIsBatchSearchActive] = useState(false);
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [showAllIngredients, setShowAllIngredients] = useState(false);
  const [batchAllocations, setBatchAllocations] = useState([]);

  const [showFormModal, setShowFormModal] = useState(false);

  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  // Form state
  const [products, setProducts] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [customer, setCustomer] = useState('');
  const [productEntries, setProductEntries] = useState([{ productId: '', lotCode: '', quantity: '' }]);
  const [suggestedLotCodes, setSuggestedLotCodes] = useState({});
  const [lotCodeStatus, setLotCodeStatus] = useState({}); // { [idx]: { valid: bool|null, inventory: number|null } }
  const [isResale, setIsResale] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editLogId, setEditLogId] = useState(null);
  // User feedback messages
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  // Load products and customers once
  useEffect(() => {
    (async () => {
      try {
        const [prodsRes, custsRes] = await Promise.all([
          service.getProducts(token),
          service.getCustomers(token)
        ]);
        setProducts(prodsRes.data);
        setAllCustomers(custsRes.data.customers || custsRes.data);
      } catch (e) {
        console.error('Error loading products/customers', e);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // Handlers for search inputs
  const handleSearchByLotCode = e => setSearchLotCode(e.target.value);
  const handleSearchByProductName = e => setProductSearchValue(e.target.value);

  // Ingredient lot code search
  const handleIngredientSearch = async () => {
    if (!ingredientLotCode) {
      setErrorModal({ show: true, message: 'Please enter an ingredient lot code.' });
      return;
    }
    try {
      const resp = await service.searchIngredient(ingredientLotCode, token);
      const { summary: data, totalQtyUsed } = resp.data;
      setIngredientTotalQtyUsed(totalQtyUsed);
      if (!data || data.length === 0) {
        setErrorModal({ show: true, message: `No products found with ingredient '${ingredientLotCode}'.` });
        return;
      }
      const breakdown = data.map(item => ({
        productName: item.productName,
        batchLotCode: item.batchLotCode,
        quantityProduced: item.quantityProduced,
        quantityAllocated: item.quantityAllocated,
        quantityInInventory: item.quantityInInventory
      }));
      const clients = data.flatMap(item =>
        item.clients.map(c => ({
          customer: c.customer,
          productName: item.productName,
          batchLotCode: item.batchLotCode,
          quantity: c.quantity,
          date: c.date,
          isDirectToConsumer: c.isDirectToConsumer || false
        }))
      );
      // Collect all BOL shipments, de-duplicating by bolId+lotCode
      // (multiple summary items can share the same batchLotCode when a batch has multiple products)
      const seen = new Set();
      const uniqueShipments = data.flatMap(item =>
        (item.shipments || []).map(s => ({ ...s, batchLotCode: item.batchLotCode }))
      ).filter(s => {
        const key = `${s.bolId}|${s.batchLotCode}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setShipments(uniqueShipments);
      setIngredientBreakdown(breakdown);
      setIngredientProducts(clients);
      setIsIngredientSearchActive(true);
      // Also fetch receiving records if module is active
      if (hasModule('ingredient.inventory')) {
        ingredientInventoryService.getByLotCode(ingredientLotCode)
          .then(logs => setReceivingLogs(Array.isArray(logs) ? logs : []))
          .catch(() => setReceivingLogs([]));
      }
    } catch (err) {
      console.error('Error searching by ingredient lot code', err);
      const msg = err.response?.data?.message || 'An unexpected error occurred while searching by ingredient lot code.';
      setErrorModal({ show: true, message: msg });
    }
  };
  // Batch lot code search
  const handleBatchSearch = async () => {
    if (!batchLotCodeInput.trim()) return;
    try {
      const resp = await service.searchBatchLotCode(batchLotCodeInput.trim(), token);
      setBatchResult(resp.data);
      setBatchAllocations(resp.data.allocations || []);
      setIsBatchSearchActive(true);
      setExpandedProductId(null);
      setShowAllIngredients(false);
    } catch (err) {
      const msg = err.response?.status === 404
        ? `Batch lot code "${batchLotCodeInput}" not found.`
        : err.response?.data?.message || 'Error searching batch lot code.';
      setErrorModal({ show: true, message: msg });
    }
  };
  const clearBatchSearch = () => {
    setBatchLotCodeInput('');
    setBatchResult(null);
    setIsBatchSearchActive(false);
    setExpandedProductId(null);
    setShowAllIngredients(false);
    setBatchAllocations([]);
  };

  const clearIngredientSearch = () => {
    setIngredientLotCode('');
    setIngredientBreakdown([]);
    setIngredientProducts([]);
    setShipments([]);
    setIsIngredientSearchActive(false);
    setReceivingLogs([]);
    setIngredientTotalQtyUsed(null);
  };

  const handleIngredientNameSearch = async () => {
    if (!ingredientNameInput.trim()) {
      setErrorModal({ show: true, message: 'Please enter an ingredient name.' });
      return;
    }
    try {
      const resp = await service.searchIngredientByName(ingredientNameInput.trim(), token);
      const { summary: data, totalQtyUsed, ingredientNames } = resp.data;
      setIngredientTotalQtyUsed(totalQtyUsed);
      setIngredientNameLabel(ingredientNames?.join(', ') || ingredientNameInput.trim());
      if (!data || data.length === 0) {
        setErrorModal({ show: true, message: `No products found using ingredient "${ingredientNameInput.trim()}".` });
        return;
      }
      const breakdown = data.map(item => ({
        productName: item.productName,
        batchLotCode: item.batchLotCode,
        quantityProduced: item.quantityProduced,
        quantityAllocated: item.quantityAllocated,
        quantityInInventory: item.quantityInInventory
      }));
      const clients = data.flatMap(item =>
        item.clients.map(c => ({
          customer: c.customer,
          productName: item.productName,
          batchLotCode: item.batchLotCode,
          quantity: c.quantity,
          date: c.date,
          isDirectToConsumer: c.isDirectToConsumer || false
        }))
      );
      const seen = new Set();
      const uniqueShipments = data.flatMap(item =>
        (item.shipments || []).map(s => ({ ...s, batchLotCode: item.batchLotCode }))
      ).filter(s => {
        const key = `${s.bolId}|${s.batchLotCode}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setShipments(uniqueShipments);
      setIngredientBreakdown(breakdown);
      setIngredientProducts(clients);
      setIsIngredientNameActive(true);
      setIsIngredientSearchActive(true);
    } catch (err) {
      console.error('Error searching by ingredient name', err);
      const msg = err.response?.data?.message || 'An unexpected error occurred while searching by ingredient name.';
      setErrorModal({ show: true, message: msg });
    }
  };

  const clearIngredientNameSearch = () => {
    setIngredientNameInput('');
    setIngredientBreakdown([]);
    setIngredientProducts([]);
    setShipments([]);
    setIsIngredientNameActive(false);
    setIsIngredientSearchActive(false);
    setReceivingLogs([]);
    setIngredientTotalQtyUsed(null);
    setIngredientNameLabel('');
  };

  // Form handlers
  const handleDateChange = val => setSelectedDate(val);
  const handleCustomerChange = e => {
    const v = e.target.value;
    setCustomer(v);
    if (v.length >= 2) {
      setCustomerSuggestions(allCustomers.filter(c => c.name.toLowerCase().includes(v.toLowerCase())));
    } else {
      setCustomerSuggestions([]);
    }
  };
  
  const handleSelectCustomer = c => { setCustomer(c.name); setCustomerSuggestions([]); };
  const handleCustomerBlur = () => setTimeout(() => setCustomerSuggestions([]), 150);
  const fetchLotCodeSuggestions = async (idx, q, productId) => {
    try {
      const resp = await service.searchBatchLotCodes(q, productId || null, token);
      setSuggestedLotCodes(prev => ({ ...prev, [idx]: resp.data.map(b => b.lotCode) }));
    } catch (e) {
      console.error('Error fetching lot code suggestions', e);
    }
  };

  const fetchLotInventory = async (idx, productId, lotCode) => {
    if (!productId || !lotCode) return;
    try {
      const resp = await service.getLotInventory(productId, lotCode, token);
      const { exists, quantity } = resp.data;
      setLotCodeStatus(prev => ({ ...prev, [idx]: { valid: exists, inventory: exists ? quantity : null } }));
    } catch (e) {
      console.error('Error fetching lot inventory', e);
    }
  };

  const handleProductEntryChange = (i, f, v) => {
    const arr = [...productEntries];
    arr[i][f] = v;
    if (f === 'productId') {
      // Reset lot code when product changes
      arr[i].lotCode = '';
      setSuggestedLotCodes(prev => ({ ...prev, [i]: [] }));
      setLotCodeStatus(prev => ({ ...prev, [i]: null }));
    }
    if (f === 'lotCode') {
      setLotCodeStatus(prev => ({ ...prev, [i]: null }));
      if (v.length >= 2) {
        fetchLotCodeSuggestions(i, v, productEntries[i].productId);
      } else {
        setSuggestedLotCodes(prev => ({ ...prev, [i]: [] }));
      }
    }
    setProductEntries(arr);
  };

  const handleLotCodeSelect = (i, code) => {
    const arr = [...productEntries];
    arr[i].lotCode = code;
    setProductEntries(arr);
    setSuggestedLotCodes(prev => ({ ...prev, [i]: [] }));
    setLotCodeStatus(prev => ({ ...prev, [i]: { valid: true, inventory: null } }));
    fetchLotInventory(i, productEntries[i].productId, code);
  };

  const handleLotCodeBlur = (idx) => {
    const entry = productEntries[idx];
    if (!entry.lotCode || lotCodeStatus[idx]?.valid === true) return;
    fetchLotInventory(idx, entry.productId, entry.lotCode);
  };

  const addProductEntry = () => setProductEntries([...productEntries, { productId: '', lotCode: '', quantity: '' }]);
  const removeProductEntry = i => {
    setProductEntries(pe => pe.filter((_, idx) => idx !== i));
    setSuggestedLotCodes(prev => { const n = { ...prev }; delete n[i]; return n; });
    setLotCodeStatus(prev => { const n = { ...prev }; delete n[i]; return n; });
  };

  // Submit form (create or update)
 const handleSubmit = async e => {
  e.preventDefault();

  // Handle submit for both create and update
  const getErrorMessage = (err) => {
    const data = err?.response?.data;

    if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
    if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim();

    // If backend sends { errors: ["...","..."] } or { errors: [{message:"..."}] }
    if (Array.isArray(data?.errors) && data.errors.length) {
      const first = data.errors[0];
      if (typeof first === 'string') return first;
      if (first?.message) return String(first.message);
    }

    // Fallbacks
    if (typeof err?.message === 'string' && err.message.trim()) return err.message.trim();
    return 'Error saving traceability record. Please try again.';
  };

  // Validate all lot codes (skipped for resale entries — no production records expected)
  const statusCopy = { ...lotCodeStatus };
  if (!isResale) {
    let hasInvalid = false;
    for (let i = 0; i < productEntries.length; i++) {
      const ent = productEntries[i];
      if (!ent.lotCode) continue;
      if (statusCopy[i]?.valid === true) continue;
      if (statusCopy[i]?.valid === false) { hasInvalid = true; continue; }
      try {
        const resp = await service.getLotInventory(ent.productId, ent.lotCode, token);
        const exists = resp.data.exists;
        statusCopy[i] = { valid: exists, inventory: exists ? resp.data.quantity : null };
        if (!exists) hasInvalid = true;
      } catch {
        statusCopy[i] = { valid: false, inventory: null };
        hasInvalid = true;
      }
    }
    setLotCodeStatus(statusCopy);

    if (hasInvalid) {
      setErrorModal({ show: true, message: 'One or more lot codes do not exist in the system. Please select valid lot codes from the autocomplete suggestions.' });
      return;
    }

    // Check stock levels
    for (let i = 0; i < productEntries.length; i++) {
      const ent = productEntries[i];
      if (!ent.lotCode || statusCopy[i]?.valid !== true) continue;
      const inv = statusCopy[i]?.inventory ?? null;
      const qty = parseInt(ent.quantity, 10) || 0;
      const label = `${ent.productName || `Product #${ent.productId}`} (lot ${ent.lotCode})`;
      if (inv !== null && inv <= 0) {
        setErrorModal({ show: true, message: `${label} has 0 units in stock. Please go to the Inventory page first and add an adjustment with the reason for the discrepancy.` });
        return;
      }
      if (inv !== null && qty > inv) {
        setErrorModal({ show: true, message: `You are trying to ship ${qty} units of ${label} but only ${inv} are in stock. Please reduce the quantity or go to the Inventory page to add an adjustment.` });
        return;
      }
    }
  }

  try {
    if (isEditing) {
      const { productId, lotCode, quantity } = productEntries[0];
      await service.updateLog(
        editLogId,
        { date: selectedDate, customer, productId, lotCode, quantity },
        token
      );
      setMessage('Traceability record updated successfully');
    } else {
      await service.createLog(
        { date: selectedDate, customer, productEntries, isResale: isResale || false },
        token
      );
      setMessage('Traceability record added successfully');
    }

    setMessageType('success');
    setTimeout(() => setMessage(''), 3000);

    setIsEditing(false);
    setEditLogId(null);
    setSelectedDate('');
    setCustomer('');
    setProductEntries([{ productId: '', lotCode: '', quantity: '' }]);
    setLotCodeStatus({});
    setIsResale(false);
    setShowFormModal(false);

    await fetchPage(1);
  } catch (err) {
    console.error('Error saving record', err);
    const serverMsg = getErrorMessage(err);

    setErrorModal({
      show: true,
      message: serverMsg
    });
  }
};

  const handleEdit = log => {
    setSelectedDate(log.date.split('T')[0]);
    setCustomer(log.customer);
    setProductEntries([{ productId: log.productId, lotCode: log.lotCode, quantity: log.quantity }]);
    setIsEditing(true); setEditLogId(log.id);
    setShowFormModal(true);
  };
  const handleCancelEdit = () => {
    setIsEditing(false); setEditLogId(null);
    setSelectedDate(''); setCustomer('');
    setProductEntries([{ productId: '', lotCode: '', quantity: '' }]);
    setLotCodeStatus({});
    setIsResale(false);
    setShowFormModal(false);
  };

  const printRef = useRef();
  const handlePrint = () => {
    // When a search is active, print the visible search results directly
    if (isBatchSearchActive || isIngredientSearchActive) {
      const style = document.createElement('style');
      style.id = '__trace-print-override';
      style.textContent = `
        @media print {
          body * { visibility: hidden !important; }
          #trace-search-results, #trace-search-results * { visibility: visible !important; }
          #trace-search-results { position: absolute; top: 0; left: 0; width: 100%; }
          .btn-link, .btn-outline-secondary { display: none !important; }
        }
      `;
      document.head.appendChild(style);
      window.print();
      document.head.removeChild(style);
      return;
    }
    // No search active — open a popup window with the formatted table
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
          <title>Traceability Logs</title>
          <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { text-align: center; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f4f4f4; }
          </style>
      </head>
      <body>
          <div id="print-content"></div>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    const container = printWindow.document.getElementById('print-content');
    const renderedContent = (
      <PrintTraceabilityView
        logs={logs}
        ingredientResults={ingredientBreakdown}
        logoUrl={companyLogoUrl}
        companyName={companyName}
        companyAddress={companyAddress}
      />
    );
    const root = createRoot(container);
    root.render(renderedContent);
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const NAVBAR_H = 48;

  return (
    <div style={{ marginTop: NAVBAR_H, height: `calc(100vh - ${NAVBAR_H}px)`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Feedback toast — fixed to viewport */}
      {message && (
        <div className="fixed-top d-flex justify-content-center" style={{ top: NAVBAR_H + 8, zIndex: 1200 }}>
          <div className={`alert alert-${messageType} alert-dismissible fade show shadow`} style={{ minWidth: 340 }}>
            {message}
            <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
          </div>
        </div>
      )}

      {/* ── Sticky top bar ── */}
      <div style={{ flexShrink: 0, background: '#fff', borderBottom: '1px solid #dee2e6', padding: '10px 24px 6px', zIndex: 100 }}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="mb-0 fw-semibold">Traceability</h5>
          <div className="d-flex gap-2">
            {logs.length > 0 && (
              <button className="btn btn-outline-secondary btn-sm" onClick={handlePrint}>Print View</button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => { setIsEditing(false); setShowFormModal(true); }}>
              + Add Record
            </button>
          </div>
        </div>

        <TraceabilitySearchPanel
        ingredientCode={ingredientLotCode}
        onIngredientCodeChange={setIngredientLotCode}
        onIngredientSearch={handleIngredientSearch}
        onIngredientClear={clearIngredientSearch}
        isIngredientActive={isIngredientSearchActive && !isIngredientNameActive}
        ingredientNameInput={ingredientNameInput}
        onIngredientNameChange={setIngredientNameInput}
        onIngredientNameSearch={handleIngredientNameSearch}
        onIngredientNameClear={clearIngredientNameSearch}
        isIngredientNameActive={isIngredientNameActive}
        batchLotCodeInput={batchLotCodeInput}
        onBatchLotCodeChange={setBatchLotCodeInput}
        onBatchSearch={handleBatchSearch}
        onBatchClear={clearBatchSearch}
        isBatchSearchActive={isBatchSearchActive}
        lotSearchValue={searchLotCode}
        onLotSearchChange={handleSearchByLotCode}
        productSearchValue={productSearchValue}
        onProductSearchChange={handleSearchByProductName}
        customerSearchValue={customerSearchValue}
        onCustomerSearchChange={e => setCustomerSearchValue(e.target.value)}
      />
      </div>{/* end sticky top bar */}

      {/* ── Scrollable content ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0px 24px' }}>

      {/* ── Add / Edit Record Modal ── */}
      {showFormModal && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{isEditing ? 'Edit Traceability Record' : 'Add Traceability Record'}</h5>
                <button className="btn-close" onClick={handleCancelEdit} />
              </div>
              <div className="modal-body">
                <TraceabilityForm
                  userRole={JSON.parse(atob(token.split('.')[1])).role}
                  isEditing={isEditing}
                  selectedDate={selectedDate}
                  onDateChange={handleDateChange}
                  customer={customer}
                  onCustomerChange={handleCustomerChange}
                  onCustomerBlur={handleCustomerBlur}
                  customerSuggestions={customerSuggestions}
                  onSelectCustomer={handleSelectCustomer}
                  productEntries={productEntries}
                  onProductEntryChange={handleProductEntryChange}
                  suggestedLotCodes={suggestedLotCodes}
                  onLotCodeSelect={handleLotCodeSelect}
                  onLotCodeBlur={handleLotCodeBlur}
                  lotCodeStatus={lotCodeStatus}
                  addProductEntry={addProductEntry}
                  removeProductEntry={removeProductEntry}
                  onSubmit={handleSubmit}
                  onCancelEdit={handleCancelEdit}
                  products={products}
                  isResale={isResale}
                  onIsResaleChange={setIsResale}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search results wrapper — used as print target */}
      <div id="trace-search-results">

      {/* Batch Lot Code Results */}
      {isBatchSearchActive && batchResult && (
        <div className="mt-3 mb-4 card border-0 shadow-sm">
          <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
            <span className="fw-semibold">Batch: <span className="font-monospace">{batchResult.batchLotCode}</span></span>
            {batchResult.batchDate && <span className="small opacity-75">Production date: {batchResult.batchDate}</span>}
          </div>
          <div className="card-body p-0">

            {/* ── Products produced ── */}
            <div className="px-3 pt-3 pb-1" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6c757d' }}>
              Products produced in this batch
            </div>
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">Product</th>
                  <th className="text-center">Produced</th>
                  <th className="text-center">Allocated</th>
                  <th className="text-center">In Stock</th>
                  <th className="text-center" style={{ width: 110 }}>Ingredients</th>
                </tr>
              </thead>
              <tbody>
                {batchResult.products.map(p => (
                  <React.Fragment key={p.productId}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      className={expandedProductId === p.productId ? 'table-primary' : ''}
                      onClick={() => setExpandedProductId(expandedProductId === p.productId ? null : p.productId)}
                    >
                      <td className="ps-3 fw-semibold">{p.productName}</td>
                      <td className="text-center">{p.quantityProduced}</td>
                      <td className="text-center">
                        <span className="badge bg-secondary">{p.quantityAllocated}</span>
                      </td>
                      <td className="text-center">
                        <span className={`badge ${p.quantityInInventory > 0 ? 'bg-success' : p.quantityInInventory < 0 ? 'bg-danger' : 'bg-secondary'}`}>
                          {p.quantityInInventory}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-secondary">{p.ingredients.length}</span>
                        <span className="ms-1 text-muted small">{expandedProductId === p.productId ? '▲' : '▼'}</span>
                      </td>
                    </tr>
                    {expandedProductId === p.productId && (
                      <tr>
                        <td colSpan={5} className="p-0 bg-light">
                          {p.ingredients.length === 0 ? (
                            <div className="px-4 py-2 text-muted small">No recipe ingredients recorded for this batch.</div>
                          ) : (
                            <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                              <thead>
                                <tr className="table-light">
                                  <th className="ps-4">Ingredient</th>
                                  <th>Lot Code</th>
                                  <th>Qty Used (kg)</th>
                                  <th>Status</th>
                                  <th>Receiving</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.ingredients.map((ing, i) => {
                                  const ingFull = (batchResult.allIngredients || []).find(
                                    a => a.ingredientId === ing.ingredientId && a.ingredientLotCode === ing.ingredientLotCode
                                  );
                                  const receiving = ingFull?.receivingInfo || [];
                                  return (
                                    <tr key={i}>
                                      <td className="ps-4">{ing.ingredientName}</td>
                                      <td className="font-monospace">{ing.ingredientLotCode || <span className="text-danger">Not recorded</span>}</td>
                                      <td>{ing.quantityKg != null ? Number(ing.quantityKg).toFixed(3) : '—'}</td>
                                      <td>
                                        <span className={`badge bg-${ing.status === 'ENTERED' ? 'success' : ing.status === 'MISSING' ? 'danger' : 'secondary'}`} style={{ fontSize: 10 }}>
                                          {ing.status}
                                        </span>
                                      </td>
                                      <td style={{ fontSize: 12 }}>
                                        {receiving.length === 0 ? <span className="text-muted">—</span> : receiving.map((r, ri) => (
                                          <div key={ri}>
                                            <span className="text-muted">{r.deliveryDate}</span>
                                            {' · '}
                                            <span>{r.supplierName}</span>
                                            {r.documentType && (
                                              <button
                                                className="btn btn-link btn-sm py-0 px-1"
                                                style={{ fontSize: 11 }}
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  try {
                                                    const { url } = await ingredientInventoryService.getDocumentUrl(r.id);
                                                    window.open(url, '_blank');
                                                  } catch { alert('Could not load document.'); }
                                                }}
                                              >
                                                📎
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* ── All batch ingredients ── (moved above customer allocations) */}
            <div className="border-top">
              <button
                className="w-100 text-start px-3 py-2 bg-transparent border-0"
                style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6c757d', cursor: 'pointer' }}
                onClick={() => setShowAllIngredients(v => !v)}
              >
                All batch ingredients ({batchResult.allIngredients.length}) {showAllIngredients ? '▲' : '▼'}
              </button>
              {showAllIngredients && (
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3">Ingredient</th>
                      <th>Lot Code</th>
                      <th>Qty Used (kg)</th>
                      <th>Status</th>
                      <th>Used by</th>
                      <th>Receiving</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchResult.allIngredients.map((ing, i) => {
                      const usedBy = batchResult.products.filter(p => p.ingredients.some(pi => pi.ingredientId === ing.ingredientId)).map(p => p.productName);
                      return (
                        <tr key={i}>
                          <td className="ps-3">{ing.ingredientName}</td>
                          <td className="font-monospace">{ing.ingredientLotCode || <span className="text-danger">Not recorded</span>}</td>
                          <td>{ing.quantityKg != null ? Number(ing.quantityKg).toFixed(3) : '—'}</td>
                          <td>
                            <span className={`badge bg-${ing.status === 'ENTERED' ? 'success' : ing.status === 'MISSING' ? 'danger' : 'secondary'}`} style={{ fontSize: 10 }}>
                              {ing.status}
                            </span>
                          </td>
                          <td className="text-muted small">{usedBy.length ? usedBy.join(', ') : '—'}</td>
                          <td style={{ fontSize: 12 }}>
                            {(ing.receivingInfo || []).length === 0 ? <span className="text-muted">—</span> : (ing.receivingInfo || []).map((r, ri) => (
                              <div key={ri}>
                                <span className="text-muted">{r.deliveryDate}</span>
                                {' · '}
                                <span>{r.supplierName}</span>
                                {r.documentType && (
                                  <button
                                    className="btn btn-link btn-sm py-0 px-1"
                                    style={{ fontSize: 11 }}
                                    onClick={async () => {
                                      try {
                                        const { url } = await ingredientInventoryService.getDocumentUrl(r.id);
                                        window.open(url, '_blank');
                                      } catch { alert('Could not load document.'); }
                                    }}
                                  >
                                    📎
                                  </button>
                                )}
                              </div>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* ── Customer Allocations ── */}
            {batchAllocations.length > 0 && (
              <div className="border-top">
                <div className="px-3 pt-3 pb-1" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6c757d' }}>
                  Customer allocations for this batch
                </div>
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3">Date</th>
                      <th>Product</th>
                      <th className="text-center">Qty</th>
                      <th>Customer</th>
                      <th>BOL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchAllocations.map((alloc, i) => (
                      <React.Fragment key={alloc.id || i}>
                        <tr>
                          <td className="ps-3" style={{ whiteSpace: 'nowrap' }}>{alloc.date ? alloc.date.split('T')[0] : '—'}</td>
                          <td>{alloc.productName}</td>
                          <td className="text-center">{alloc.quantity}</td>
                          <td>
                            {alloc.customer}
                            {alloc.isDirectToConsumer && (
                              <span className="badge bg-info text-dark ms-1" style={{ fontSize: 9 }}>Direct to Consumer</span>
                            )}
                          </td>
                          <td>
                            {alloc.bolId ? (
                              <>
                                <a href={`/bol/print?id=${alloc.bolId}`} target="_blank" rel="noreferrer" className="font-monospace fw-semibold me-1" style={{ fontSize: 13 }}>
                                  {alloc.bolNumber}
                                </a>
                                <span className={`badge ${alloc.bolStatus === 'signed' ? 'bg-primary' : 'bg-success'}`} style={{ fontSize: 10 }}>
                                  {alloc.bolStatus}
                                </span>
                                {alloc.scannedDocumentUrl && (
                                  <a href={alloc.scannedDocumentUrl} target="_blank" rel="noreferrer" className="ms-1 text-secondary small">📎</a>
                                )}
                              </>
                            ) : <span className="text-muted">—</span>}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Ingredient Search Results */}
      {isIngredientSearchActive && ingredientBreakdown.length > 0 && (
        <div className="mt-4">
          <div className="d-flex align-items-baseline gap-3 mb-2">
            <h4 className="mb-0">
              {isIngredientNameActive
                ? <>Ingredient: <span className="font-monospace">{ingredientNameLabel}</span></>
                : <>Ingredient lot code: <span className="font-monospace">{ingredientLotCode}</span></>
              }
            </h4>
            {ingredientTotalQtyUsed != null && (
              <span className="text-muted small">Total used: <strong>{Number(ingredientTotalQtyUsed).toFixed(3)} kg</strong></span>
            )}
          </div>

          <h6 className="text-uppercase text-muted fw-semibold" style={{ fontSize: 11, letterSpacing: '0.05em' }}>Products produced with this ingredient</h6>
          <table className="table table-bordered table-sm">
            <thead className="table-light">
              <tr>
                <th>Product</th>
                <th>Batch</th>
                <th className="text-center">Produced</th>
                <th className="text-center">Allocated</th>
                <th className="text-center">In Inventory</th>
              </tr>
            </thead>
            <tbody>
              {ingredientBreakdown.map((item, i) => (
                <tr key={i}>
                  <td>{item.productName}</td>
                  <td className="font-monospace small">{item.batchLotCode}</td>
                  <td className="text-center">{item.quantityProduced}</td>
                  <td className="text-center">{item.quantityAllocated}</td>
                  <td className="text-center">
                    <span className={`badge ${item.quantityInInventory > 0 ? 'bg-success' : item.quantityInInventory < 0 ? 'bg-danger' : 'bg-secondary'}`}>
                      {item.quantityInInventory}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="table-light">
              <tr className="fw-semibold">
                <td colSpan={2}>Totals</td>
                <td className="text-center">{ingredientBreakdown.reduce((s, i) => s + i.quantityProduced, 0)}</td>
                <td className="text-center">{ingredientBreakdown.reduce((s, i) => s + i.quantityAllocated, 0)}</td>
                <td className="text-center">{ingredientBreakdown.reduce((s, i) => s + i.quantityInInventory, 0)}</td>
              </tr>
            </tfoot>
          </table>

          {/* Receiving Records — shown when ingredient.inventory module is active, moved up for prominence */}
          {hasModule('ingredient.inventory') && (
            <div className="mt-3 mb-4">
              <h6 className="text-uppercase text-muted fw-semibold" style={{ fontSize: 11, letterSpacing: '0.05em' }}>Ingredient receiving records</h6>
              {receivingLogs.length === 0 ? (
                <p className="text-muted" style={{ fontSize: 13 }}>No receiving records found for lot code "{ingredientLotCode}".</p>
              ) : (
                <table className="table table-sm table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Supplier</th>
                      <th>Lot Code(s)</th>
                      <th>Qty Received (kg)</th>
                      <th>Temp</th>
                      <th>Document</th>
                      <th>Received By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivingLogs.map(log => (
                      <tr key={log.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{log.deliveryDate}</td>
                        <td>{log.supplierName}</td>
                        <td>
                          {(log.LotCodes || []).map(lc => (
                            <span key={lc.id} className="badge text-bg-light border me-1">{lc.lotCode} ({Number(lc.quantityKg).toFixed(3)} kg)</span>
                          ))}
                        </td>
                        <td>{Number(log.totalQuantityKg).toFixed(3)}</td>
                        <td>
                          {log.isTemperatureControlled
                            ? `${log.receivedTemperature != null ? log.receivedTemperature + '°C' : '—'}${log.isFrozen ? ' ❄' : ''}`
                            : '—'}
                        </td>
                        <td>
                          {log.documentType ? (
                            <button
                              className="btn btn-outline-secondary btn-sm py-0 px-1"
                              style={{ fontSize: 11 }}
                              onClick={async () => {
                                try {
                                  const { url } = await ingredientInventoryService.getDocumentUrl(log.id);
                                  window.open(url, '_blank');
                                } catch { alert('Could not load document.'); }
                              }}
                            >
                              📎 {({ bol: 'BOL', receipt: 'Receipt', other: 'Doc' })[log.documentType] || 'Doc'}
                            </button>
                          ) : '—'}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {log.receivedByName || '—'}
                          {log.verifiedByName && <div className="text-muted">Verified: {log.verifiedByName}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <h6 className="text-uppercase text-muted fw-semibold" style={{ fontSize: 11, letterSpacing: '0.05em' }}>Client allocations</h6>
          <table className="table table-bordered table-sm">
            <thead className="table-light">
              <tr><th>Customer</th><th>Product</th><th>Batch</th><th className="text-center">Quantity</th><th>Date</th></tr>
            </thead>
            <tbody>
              {ingredientProducts.map((c, i) => (
                <tr key={i}><td>{c.customer}</td><td>{c.productName}</td><td className="font-monospace small">{c.batchLotCode}</td><td className="text-center">{c.quantity}</td><td>{c.date.split('T')[0]}</td></tr>
              ))}
            </tbody>
          </table>

          {/* BOL Shipments — shown when shipping.bol module is active */}
          {hasModule('shipping.bol') && shipments.length > 0 && (
            <div className="mt-3">
              <h6 className="text-uppercase text-muted fw-semibold" style={{ fontSize: 11, letterSpacing: '0.05em' }}>Shipments (Bills of Lading)</h6>
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>BOL #</th>
                    <th>Ship Date</th>
                    <th>Consignee</th>
                    <th>Lot Code</th>
                    <th className="text-center">Cases</th>
                    <th className="text-center">Units</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s, i) => (
                    <tr key={i}>
                      <td className="font-monospace fw-semibold">
                        <a href={`/bol/print?id=${s.bolId}`} target="_blank" rel="noreferrer">{s.bolNumber}</a>
                        {s.scannedDocumentUrl && (
                          <a href={s.scannedDocumentUrl} target="_blank" rel="noreferrer" className="ms-2 text-secondary small" title="View uploaded document">📎</a>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{s.shipDate || '—'}</td>
                      <td>{s.consigneeName || '—'}</td>
                      <td className="font-monospace small">{s.batchLotCode}</td>
                      <td className="text-center">{s.totalCases}</td>
                      <td className="text-center">{s.totalUnits ?? '—'}</td>
                      <td><span className={`badge ${s.status === 'signed' ? 'bg-primary' : 'bg-success'}`}>{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      </div>{/* end trace-search-results */}

      {/* Main Logs & Pagination hidden during any active search */}
      {!isIngredientSearchActive && !isBatchSearchActive && (
        <>
          <TraceabilityTable
            logs={logs}
            filteredLogs={[]}
            isFiltered={false}
            userRole={JSON.parse(atob(token.split('.')[1])).role}
            onEdit={handleEdit}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            onPrev={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        </>
      )}
      </div>{/* end scrollable content */}

      {/* Error Modal */}
      <Modal show={errorModal.show} onHide={() => setErrorModal({ show: false, message: '' })}>
        <Modal.Header closeButton style={{ backgroundColor: '#dc3545', color: 'white' }}>
          <Modal.Title style={{ color: 'white' }}>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorModal.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setErrorModal({ show: false, message: '' })}>OK</Button>
        </Modal.Footer>
      </Modal>

      {/* Hidden Print Section */}
      <div ref={printRef} style={{ display: 'none' }}>
        <PrintTraceabilityView
          logs={logs}
          ingredientResults={ingredientBreakdown}
          logoUrl={companyLogoUrl}
          companyName={companyName}
          companyAddress={companyAddress}
        />
      </div>
    </div>
  );
}
